import type { ReactNode } from 'react';
import { Avatar } from '@cortex/web-design-system/components/ui/Avatar';
import type { CuttysarkLiveCandidate } from '@cortex/domain-product-innovation-lab-types';
import { candidatePhoto } from './cuttysark-helpers.js';

/** @graph-connects none */
interface CandidateCardProps {
  candidate: CuttysarkLiveCandidate;
  /** 部門名（候補者の divisionId を引いた表示名）。 */
  divisionName: string;
  /** いま発表している人として強調する。 */
  highlighted?: boolean;
  /** カード下部に差し込む内容（資料リンク・採点欄など）。 */
  children?: ReactNode;
}

/**
 * 候補者 1 人のカード。写真（未登録ならプロフィール写真）・二つ名・名前・部門を出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞の候補者を写真・二つ名・名前・部門で表示するカード
 * @graph-connects candidatePhoto [calls] 表示する写真の決定
 */
export function CandidateCard({
  candidate,
  divisionName,
  highlighted = false,
  children,
}: CandidateCardProps): ReactNode {
  return (
    <div
      className={`rounded-lg border p-3 ${
        highlighted
          ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/5 shadow-md'
          : 'border-[var(--color-border)] bg-[var(--color-bg-surface)]'
      }`}
    >
      <div className="flex items-center gap-3">
        <Avatar src={candidatePhoto(candidate) || undefined} name={candidate.name} size="lg" />
        <div className="min-w-0">
          {candidate.title !== '' && (
            <p className="truncate text-xs font-semibold text-[var(--color-accent-primary)]">
              {candidate.title}
            </p>
          )}
          <p className="truncate text-base font-semibold text-[var(--color-text-primary)]">
            {candidate.name}
          </p>
          <p className="truncate text-xs text-[var(--color-text-muted)]">{divisionName}</p>
        </div>
      </div>
      {candidate.intro !== '' && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
          {candidate.intro}
        </p>
      )}
      {children}
    </div>
  );
}
